import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Bar, Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

const PERIODS = ['today', 'yesterday', 'week'];
const PERIOD_LABELS = { today: 'Idag', yesterday: 'Igår', week: 'Vecka' };

const BORDER_COLORS = {
    'SE-FI': '#4e79a7',
    'SE-NO': '#59a14f',
    'SE-DK': '#f28e2b',
    'SE-PL': '#e15759',
    'SE-LT': '#76b7b2',
    'SE-DE': '#b07aa1',
};

const UtilizationBar = ({ value }) => {
    const pct = Math.min(value || 0, 100);
    const color = pct > 80 ? '#ef4444' : pct > 60 ? '#f59e0b' : '#22c55e';
    return (
        <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
            </div>
            <span className="text-xs text-gray-400 w-10 text-right">{pct}%</span>
        </div>
    );
};

const DataBadge = ({ available }) => (
    <span className={`text-xs px-1.5 py-0.5 rounded ${available ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-500'}`}>
        {available ? '✓' : '–'}
    </span>
);

export default function TransmissionFlowsDashboard() {
    const [period, setPeriod] = useState('today');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedBorder, setSelectedBorder] = useState('SE-FI');

    const fetchData = async (p) => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get(`/api/transmission-flows?period=${p}`);
            setData(res.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Kunde inte hämta transmissionsflöden');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(period); }, [period]);

    const buildNetFlowChart = () => {
        if (!data?.netFlows) return null;
        const borders = Object.keys(data.netFlows).filter(b => data.netFlows[b].hasData);
        if (borders.length === 0) return null;

        const maxLen = Math.max(...borders.map(b => data.netFlows[b].hourly?.length || 0));
        const labels = Array.from({ length: maxLen }, (_, i) => `H${i + 1}`);

        const datasets = borders.map(b => ({
            label: b,
            data: data.netFlows[b].hourly || [],
            borderColor: BORDER_COLORS[b] || '#888',
            backgroundColor: (BORDER_COLORS[b] || '#888') + '20',
            fill: false,
            tension: 0.3,
            pointRadius: 2,
        }));

        return { labels, datasets };
    };

    const buildBorderDetailChart = () => {
        if (!data?.byBorder?.[selectedBorder]) return null;
        const borderData = data.byBorder[selectedBorder];
        const fwdFlow = borderData.find(f => f.from.startsWith('SE'));
        const revFlow = borderData.find(f => f.to.startsWith('SE'));

        const len = Math.max(
            fwdFlow?.physicalFlows?.hourly?.length || 0,
            revFlow?.physicalFlows?.hourly?.length || 0,
        );
        if (len === 0) return null;

        const labels = Array.from({ length: len }, (_, i) => `H${i + 1}`);
        const datasets = [];

        if (fwdFlow?.physicalFlows?.hourly?.length) {
            datasets.push({
                label: `${fwdFlow.from}→${fwdFlow.to} (Export)`,
                data: fwdFlow.physicalFlows.hourly,
                backgroundColor: '#22c55e50',
                borderColor: '#22c55e',
                borderWidth: 1,
                type: 'bar',
            });
        }
        if (revFlow?.physicalFlows?.hourly?.length) {
            datasets.push({
                label: `${revFlow.from}→${revFlow.to} (Import)`,
                data: revFlow.physicalFlows.hourly.map(v => -v),
                backgroundColor: '#ef444450',
                borderColor: '#ef4444',
                borderWidth: 1,
                type: 'bar',
            });
        }
        if (fwdFlow?.capacity?.hourly?.length) {
            datasets.push({
                label: 'Capacity',
                data: fwdFlow.capacity.hourly,
                borderColor: '#f59e0b',
                borderDash: [5, 3],
                fill: false,
                pointRadius: 0,
                tension: 0.2,
                type: 'line',
                yAxisID: 'y',
            });
        }

        return { labels, datasets };
    };

    const netChart = buildNetFlowChart();
    const detailChart = buildBorderDetailChart();

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { labels: { color: '#9ca3af', boxWidth: 12 } },
        },
        scales: {
            x: { ticks: { color: '#6b7280' }, grid: { color: 'rgba(255,255,255,0.05)' } },
            y: {
                ticks: { color: '#6b7280', callback: v => `${v.toLocaleString()} MW` },
                grid: { color: 'rgba(255,255,255,0.05)' }
            }
        }
    };

    const borders = data ? Object.keys(data.byBorder || {}) : [];

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 text-white p-6">
            <div className="max-w-6xl mx-auto">
                <h1 className="text-3xl font-bold mb-2">Transmissionsflöden</h1>
                <p className="text-gray-400 mb-6">
                    Fysiska gränsöverskridande elflöden &amp; kapacitetsutnyttjande · ENTSO-E A11/A65/A61
                </p>

                {/* Period Selector */}
                <div className="flex gap-2 mb-6">
                    {PERIODS.map(p => (
                        <button
                            key={p}
                            onClick={() => setPeriod(p)}
                            className={`px-4 py-1.5 rounded-lg text-sm capitalize transition-colors ${
                                period === p ? 'bg-blue-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                            }`}
                        >
                            {p}
                        </button>
                    ))}
                </div>

                {loading && (
                    <div className="text-center py-20 text-gray-400">
                        <div className="animate-spin h-10 w-10 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
                        Hämtar alla sammankopplingar — detta kan ta en stund...
                    </div>
                )}

                {error && (
                    <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-4 text-red-300">{error}</div>
                )}

                {!loading && !error && data && (
                    <>
                        {/* Data Quality Banner */}
                        {data.metadata?.dataQuality && (
                            <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 flex flex-wrap gap-4 text-sm text-gray-400 mb-6">
                                <span>Flöden med data: <b className="text-white">{data.metadata.dataQuality.flowsWithData}/{data.metadata.dataQuality.totalFlows}</b></span>
                                <span>Kapacitetskällor: <b className="text-white">A65: {data.metadata.dataQuality.capacitySources?.A65 || 0} · A61: {data.metadata.dataQuality.capacitySources?.A61 || 0}</b></span>
                                <span>Täckning: <b className="text-white">{data.metadata.dataQuality.completenessPercentage}%</b></span>
                            </div>
                        )}

                        {/* Border Summary Cards */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                            {borders.map(border => {
                                const net = data.netFlows?.[border];
                                const borderFlows = data.byBorder[border];
                                const fwd = borderFlows?.find(f => f.from.startsWith('SE'));
                                const utilization = fwd?.utilization?.average;
                                return (
                                    <button
                                        key={border}
                                        onClick={() => setSelectedBorder(border)}
                                        className={`bg-white/5 border rounded-xl p-4 text-left transition-colors ${
                                            selectedBorder === border ? 'border-blue-500' : 'border-white/10 hover:border-white/30'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-semibold text-sm">{border}</span>
                                            <span
                                                className="w-2 h-2 rounded-full"
                                                style={{ backgroundColor: BORDER_COLORS[border] || '#888' }}
                                            />
                                        </div>
                                        <p className={`text-lg font-bold ${(net?.average || 0) > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                            {(net?.average || 0) > 0 ? '+' : ''}{Math.round(net?.average || 0).toLocaleString()} MW
                                        </p>
                                        <p className="text-xs text-gray-500 mb-2">medel nettoflöde</p>
                                        {utilization !== undefined && (
                                            <UtilizationBar value={utilization} />
                                        )}
                                        <div className="flex gap-2 mt-2">
                                            <span className="text-xs text-gray-500">Flöden</span>
                                            <DataBadge available={net?.hasData} />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Net Flows Chart */}
                        {netChart && (
                            <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
                                <h2 className="text-lg font-semibold mb-4">Nettoflöden per gräns (MW) — positivt = Sverige exporterar</h2>
                                <div className="h-64">
                                    <Line data={netChart} options={chartOptions} />
                                </div>
                            </div>
                        )}

                        {/* Border Detail */}
                        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold">{selectedBorder} — Fysiska flöden &amp; kapacitet</h2>
                                <div className="flex gap-2">
                                    {borders.map(b => (
                                        <button
                                            key={b}
                                            onClick={() => setSelectedBorder(b)}
                                            className={`px-2.5 py-1 rounded text-xs transition-colors ${
                                                selectedBorder === b ? 'text-white' : 'bg-white/10 text-gray-400 hover:bg-white/20'
                                            }`}
                                            style={selectedBorder === b ? { backgroundColor: BORDER_COLORS[b] } : {}}
                                        >
                                            {b}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="h-64">
                                {detailChart ? (
                                    <Bar data={detailChart} options={{ ...chartOptions, plugins: { ...chartOptions.plugins, legend: { labels: { color: '#9ca3af', boxWidth: 12 } } } }} />
                                ) : (
                                    <div className="h-full flex items-center justify-center text-gray-600">
                                        Ingen fysisk flödesdata tillgänglig för {selectedBorder}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
